import React, { useState } from 'react';
import {
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Button,
  Paper,
  Chip,

} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { FilterCondition, FilterOperator } from '../types/reportBuilder.types';
import { OrganizationField } from '../types/reportBuilder.types';
import { SelectedQuestion } from '../types/reportBuilder.types';

interface FilterBuilderProps {
  filters: FilterCondition[];
  onFiltersChange: (filters: FilterCondition[]) => void;
  availableFields: OrganizationField[];
  availableQuestions: SelectedQuestion[];
}

const FilterBuilder: React.FC<FilterBuilderProps> = ({
  filters,
  onFiltersChange,
  availableFields,
  availableQuestions,
}) => {
  const [newFilter, setNewFilter] = useState<Partial<FilterCondition>>({
    sourceType: 'field',
    operator: 'is',
    value: '',
  });

  const operators: { value: FilterOperator; label: string }[] = [
    { value: 'is', label: 'Es igual a' },
    { value: 'isNot', label: 'No es igual a' },
    { value: 'contains', label: 'Contiene' },
    { value: 'startsWith', label: 'Comienza con' },
    { value: 'endsWith', label: 'Termina con' },
    { value: 'between', label: 'Entre' },
    { value: 'gt', label: 'Mayor que' },
    { value: 'less', label: 'Menor que' },
  ];

  const handleAddFilter = () => {
    if (!newFilter.sourceId || !newFilter.operator) {
      return;
    }

    const filter: FilterCondition = {
      sourceType: newFilter.sourceType || 'field',
      sourceId: newFilter.sourceId,
      operator: newFilter.operator,
      value: newFilter.value,
      valueType: newFilter.valueType || 'string',
    };

    onFiltersChange([...filters, filter]);
    setNewFilter({
      sourceType: 'field',
      operator: 'is',
      value: '',
    });
  };

  const handleRemoveFilter = (index: number) => {
    onFiltersChange(filters.filter((_, i) => i !== index));
  };

  const getSourceLabel = (sourceType: string, sourceId: string): string => {
    if (sourceType === 'field') {
      const field = availableFields.find(f => f === sourceId);
      return field || sourceId;
    } else {
      const question = availableQuestions.find(q => `${q.programVersionItemId}:${q.question.uuid}` === sourceId);
      return question ? question.question.text : sourceId;
    }
  };

  return (
    <Box>
      <Typography variant="subtitle2" gutterBottom>
        Filtros
      </Typography>

      {filters.length > 0 && (
        <Box sx={{ mb: 2 }}>
          {filters.map((filter, index) => (
            <Chip
              key={index}
              label={`${getSourceLabel(filter.sourceType, filter.sourceId)} ${filter.operator} ${filter.value}`}
              onDelete={() => handleRemoveFilter(index)}
              sx={{ mr: 1, mb: 1 }}
            />
          ))}
        </Box>
      )}

      <Paper variant="outlined" sx={{ p: 2 }}>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Origen</InputLabel>
            <Select
              value={newFilter.sourceType || 'field'}
              onChange={(e) =>
                setNewFilter({ ...newFilter, sourceType: e.target.value as 'field' | 'question', sourceId: '' })
              }
              label="Origen"
            >
              <MenuItem value="field">Campo</MenuItem>
              <MenuItem value="question">Pregunta</MenuItem>
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Campo/Pregunta</InputLabel>
            <Select
              value={newFilter.sourceId || ''}
              onChange={(e) => setNewFilter({ ...newFilter, sourceId: e.target.value })}
              label="Campo/Pregunta"
            >
              {newFilter.sourceType === 'field'
                ? availableFields.map((field) => (
                    <MenuItem key={field} value={field}>
                      {field}
                    </MenuItem>
                  ))
                : availableQuestions.map((q) => (
                    <MenuItem
                      key={`${q.programVersionItemId}:${q.question.uuid}`}
                      value={`${q.programVersionItemId}:${q.question.uuid}`}
                    >
                      {q.question.text}
                    </MenuItem>
                  ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Operador</InputLabel>
            <Select
              value={newFilter.operator || 'is'}
              onChange={(e) => setNewFilter({ ...newFilter, operator: e.target.value as FilterOperator })}
              label="Operador"
            >
              {operators.map((op) => (
                <MenuItem key={op.value} value={op.value}>
                  {op.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {newFilter.operator === 'between' ? (
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <TextField
                size="small"
                label="Valor mínimo"
                value={Array.isArray(newFilter.value) ? newFilter.value[0] : ''}
                onChange={(e) =>
                  setNewFilter({
                    ...newFilter,
                    value: [e.target.value, Array.isArray(newFilter.value) ? newFilter.value[1] : ''],
                  })
                }
                sx={{ width: 120 }}
              />
              <Typography>-</Typography>
              <TextField
                size="small"
                label="Valor máximo"
                value={Array.isArray(newFilter.value) ? newFilter.value[1] : ''}
                onChange={(e) =>
                  setNewFilter({
                    ...newFilter,
                    value: [Array.isArray(newFilter.value) ? newFilter.value[0] : '', e.target.value],
                  })
                }
                sx={{ width: 120 }}
              />
            </Box>
          ) : (
            <TextField
              size="small"
              label="Valor"
              value={newFilter.value || ''}
              onChange={(e) => setNewFilter({ ...newFilter, value: e.target.value })}
              sx={{ minWidth: 150 }}
            />
          )}

          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={handleAddFilter}
            disabled={!newFilter.sourceId || !newFilter.operator}
          >
            Agregar
          </Button>
        </Box>
      </Paper>
    </Box>
  );
};

export default FilterBuilder;
